import { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { getMyBlockedUsers, updateMyBlockedUsers } from '@cobu/api-client/blocked-users'
import type { RootState } from '../store'
import { Strings } from '../constants/Strings'
import { showErrorMessage } from '../utils/messageToast';

export function useBlockedUsers() {
  const user = useSelector((s: RootState) => s.auth.user)
  const qc = useQueryClient()
  const [isUnblocking, setIsUnblocking] = useState(false)
  const [unblockAlertOpen, setUnblockAlertOpen] = useState(false)
  const [userToUnblock, setUserToUnblock] = useState<{ id: string; name: string } | null>(null)
  const strings = Strings.get();
  const { data, isLoading, isFetching, error, refetch } = useQuery<string[]>({
    queryKey: ['blocked-users'],
    queryFn: () => getMyBlockedUsers(),
  })

  const blockedUserIds = useMemo(() => data ?? [], [data])

  const unblockUser = useCallback(
    async (blockedUserId: string) => {
      if (!user?.uuid || !blockedUserIds) return
      setIsUnblocking(true)
      try {
        const next = blockedUserIds.filter((x) => x !== blockedUserId)
        await updateMyBlockedUsers(user.uuid, next)
        await qc.invalidateQueries({ queryKey: ['blocked-users'] })
        await refetch()
      } finally {
        setIsUnblocking(false)
      }
    },
    [user?.uuid, blockedUserIds, qc, refetch]
  )

  const handleUnblockUser = useCallback(
    (blockedUserId: string, userName?: string) => {
      const name = userName || 'user'
      setUserToUnblock({ id: blockedUserId, name })
      setUnblockAlertOpen(true)
    },
    []
  )

  const confirmUnblockUser = useCallback(async () => {
    if (!userToUnblock) return

    setUnblockAlertOpen(false)
    try {
      await unblockUser(userToUnblock.id)
    } catch (error) {
      showErrorMessage(strings.common.somethingWentWrong)
    } finally {
      setUserToUnblock(null)
    }
  }, [userToUnblock, unblockUser, strings])

  const cancelUnblockUser = useCallback(() => {
    setUnblockAlertOpen(false)
    setUserToUnblock(null)
  }, [])

  return {
    blockedUserIds,
    isLoading: isLoading || isFetching,
    isUnblocking,
    error,
    handleUnblockUser,
    refetch,
    // Alert state
    unblockAlertOpen,
    setUnblockAlertOpen,
    userToUnblock,
    confirmUnblockUser,
    cancelUnblockUser,
  }
}
